import { BybAvatar, BybLeagueProgress } from "@Components/common/byb-ds";
import { BybButtonVR } from "@Components/game/common";
import { VRReleaseNotesIndicator } from "@Components/game/release-notes/";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import getLeagueInfo from "@Containers/shared/helpers/get-league-info";
import { Actions, Events } from "@Context/common/general/context";
import { formatName } from "@Helpers/formatters";
import { getLeagueColor } from "@Helpers/league-utils";
import useGlobalContext from "@hooks/common/general/use-global-context";
import useDemoAccount from "@hooks/common/user/use-demo-account";
import { useUserAccountData } from "@hooks/index";
import useI18n from "@i18n";
import { Box, Button, Popover, Stack, Typography, useTheme } from "@mui/material";
import { hexToRgba } from "@Shared/utilities/hexToRgba";
import { isNumber } from "lodash-es";
import { useEffect, useState } from "react";
import { RadialBar, RadialBarChart, ResponsiveContainer } from "recharts";
import { DialogSectionDark } from "../common/dialog/styled-components";
import WhatsNewButton from "../release-notes/vr-whats-new-button";
import HeaderPopoverDemoAlert from "./header-popover-demo-alert";
import HeaderPopoverInfoStack from "./header-popover-info-stack";
import { AvatarWrapper, ProfileButton } from "./styled-components";
import { faPlay } from "@fortawesome/pro-regular-svg-icons";
import Icon from "@/components/generic/icon";

const HeaderPopover = () => {
  const { globalState, dispatch } = useGlobalContext();
  const { i18n } = useI18n();
  const theme = useTheme();
  const { profileImage, playerBybIQ, rank, loading: userDataLoading } = useUserAccountData();
  const { isDemo } = useDemoAccount();

  const [playersLeague, setPlayersLeague] = useState<any>(null);
  const [playersDivision, setPlayersDivision] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [profilePopoverAnchorEl, setProfilePopoverAnchorEl] = useState<HTMLButtonElement | null>(null);
  const profilePopoverOpen = Boolean(profilePopoverAnchorEl);

  const updateProgression = (updatedRating: number, progressionConfig: any) => {
    const leagueInfo = getLeagueInfo(progressionConfig.leagues, updatedRating);
    setPlayersLeague(leagueInfo.league);
    setPlayersDivision(leagueInfo.division);

    // Moved progress calculation to here to align with all progression config && userRating refreshes
    const divisionRating = leagueInfo.division?.rating_point || 0;
    const nextDivisionRating = leagueInfo.nextDivision?.rating_point || 0;
    const progress = ((updatedRating - divisionRating) / (nextDivisionRating - divisionRating)) * 100;
    setProgress(progress);
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setProfilePopoverAnchorEl(event.currentTarget);
  };

  const handleSwitchProfileClick = () => {
    setProfilePopoverAnchorEl(null);
    Events.app.navigate.emit({ path: "/select-profile" });
  };

  const handleSettingsClick = () => {
    setProfilePopoverAnchorEl(null);
    Events.app.navigate.emit({ path: "/settings" });
  };

  const handleLogoutClick = () => {
    dispatch({ type: Actions.FirebaseActions.Logout });
  };

  useEffect(() => {
    if (isNumber(globalState.user.userRating) && globalState.user.progressionConfig) {
      updateProgression(globalState.user.userRating, globalState.user.progressionConfig);
    }
  }, [globalState.user.userRating, globalState.user.progressionConfig]);

  return (
    <>
      <HapticsButtonWrapper>
        <ProfileButton onClick={handleProfileClick}>
          <ResponsiveContainer>
            <RadialBarChart
              innerRadius={42}
              data={[{ name: "score", score: progress, maxScore: 100 }]}
              margin={{ top: -5, right: 0, left: 0, bottom: -5 }}
              startAngle={90}
              endAngle={-270}
            >
              <RadialBar dataKey="maxScore" maxBarSize={0} />
              <RadialBar
                dataKey="score"
                cornerRadius={18}
                fill={!!playersLeague ? getLeagueColor(playersLeague.name, theme) : theme.palette.dsPrimary.v300}
              />
            </RadialBarChart>
          </ResponsiveContainer>
          <AvatarWrapper>
            <BybAvatar src={profileImage || ""} size={6} user={globalState.user} />
            <VRReleaseNotesIndicator />
          </AvatarWrapper>
        </ProfileButton>
      </HapticsButtonWrapper>

      <Popover
        id="profile-popover-vr"
        open={profilePopoverOpen}
        anchorEl={profilePopoverAnchorEl}
        onClose={() => setProfilePopoverAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        disableScrollLock
        sx={{
          "& .MuiPopover-paper": {
            backgroundColor: hexToRgba(theme.palette.dsSurface.v100, theme.palette.dsOpacity.high),
            borderRadius: theme.spacing(3),
            backdropFilter: "blur(16px)",
            backgroundImage: "none",
          },
        }}
      >
        <Stack spacing={4} sx={{ padding: theme.spacing(5, 3, 4) }}>
          <Stack spacing={2} sx={{ alignItems: "center" }}>
            <BybAvatar src={profileImage || ""} league={playersLeague?.name} user={globalState.user} size={10} />
            <Stack spacing={0.5} alignItems="center">
              <Typography variant="text1xl600" color="dsBase.v200">
                {formatName(globalState.user.first, globalState.user.last)}
              </Typography>
              {!isDemo && (
                <Typography variant="textLg500" color="dsNeutral.v200">
                  {i18n("league.specific", { league: playersLeague?.name })}
                </Typography>
              )}
            </Stack>
          </Stack>

          {!isDemo ? (
            <Stack spacing={2}>
              <DialogSectionDark>
                <Stack spacing={2} sx={{ padding: theme.spacing(2, 1, 2.5) }}>
                  <Typography variant="textLg600" color="dsBase.v200">
                    {i18n("division.specific", { division: playersDivision?.name })}
                  </Typography>

                  <Box sx={{ width: theme.spacing(40) }}>
                    <BybLeagueProgress
                      league={playersLeague?.name}
                      division={Number(playersDivision?.name)}
                      divisions={playersLeague?.divisions.length || 0}
                    />
                  </Box>
                </Stack>
              </DialogSectionDark>
              <Stack spacing={2} direction="row">
                <DialogSectionDark sx={{ width: "fill-available" }}>
                  <HeaderPopoverInfoStack value={rank} label={i18n("header.menu.rank")} loading={userDataLoading} />
                </DialogSectionDark>
                <DialogSectionDark sx={{ width: "fill-available" }}>
                  <HeaderPopoverInfoStack value={playerBybIQ} label={i18n("header.menu.bybiq")} loading={userDataLoading} />
                </DialogSectionDark>
              </Stack>
            </Stack>
          ) : (
            <HeaderPopoverDemoAlert />
          )}

          <Stack spacing={1.5}>
            <WhatsNewButton size="small" fullWidth />
            <Stack spacing={1.5} direction="row">
              <Stack width="100%">
                <Button color="info" onClick={handleSwitchProfileClick} size="small" fullWidth>
                  {i18n("header.menu.switch-profile")}
                </Button>
              </Stack>
              <Stack width="100%">
                <Button color="info" onClick={handleSettingsClick} size="small" fullWidth>
                  {i18n("header.menu.settings")}
                </Button>
              </Stack>
            </Stack>
            <Button color="secondary" onClick={handleLogoutClick} size="small" fullWidth>
              {i18n("header.menu.logout")}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

export default HeaderPopover;
